// File: frontend/src/components/chat/MarkdownRenderer.js

import React from 'react';
import styles from './MarkdownRenderer.module.css';

const MarkdownRenderer = ({ content }) => {
  // Parse markdown to React elements
  const parseMarkdown = (text) => {
    const elements = [];
    const lines = text.split('\n');
    let i = 0;
    
    while (i < lines.length) {
      const line = lines[i];
      
      // Headers (### Header)
      if (line.startsWith('### ')) {
        elements.push(
          <h4 key={`h4-${i}`} className={styles.heading4}>
            {parseInline(line.substring(4))}
          </h4>
        );
        i++;
        continue;
      }
      
      // Headers (## Header)
      if (line.startsWith('## ')) {
        elements.push(
          <h3 key={`h3-${i}`} className={styles.heading3}>
            {parseInline(line.substring(3))}
          </h3>
        );
        i++;
        continue;
      }
      
      // Headers (# Header)
      if (line.startsWith('# ')) {
        elements.push(
          <h2 key={`h2-${i}`} className={styles.heading2}>
            {parseInline(line.substring(2))}
          </h2>
        );
        i++;
        continue;
      }
      
      // Numbered lists (1. Item)
      if (line.match(/^\d+\.\s/)) {
        const listItems = [];
        while (i < lines.length && lines[i].match(/^\d+\.\s/)) {
          listItems.push(
            <li key={`li-${i}`}>
              {parseInline(lines[i].replace(/^\d+\.\s/, ''))}
            </li>
          );
          i++;
        }
        elements.push(
          <ol key={`ol-${i}`} className={styles.orderedList}>
            {listItems}
          </ol>
        );
        continue;
      }
      
      // Bullet lists (- Item or * Item)
      if (line.match(/^[-*]\s/)) {
        const listItems = [];
        while (i < lines.length && lines[i].match(/^[-*]\s/)) {
          listItems.push(
            <li key={`li-${i}`}>
              {parseInline(lines[i].substring(2))}
            </li>
          );
          i++;
        }
        elements.push(
          <ul key={`ul-${i}`} className={styles.unorderedList}>
            {listItems}
          </ul>
        );
        continue;
      }
      
      // Code blocks (```code```)
      if (line.startsWith('```')) {
        const codeLines = [];
        i++; // Skip opening ```
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        i++; // Skip closing ```
        elements.push(
          <pre key={`code-${i}`} className={styles.codeBlock}>
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        continue;
      }
      
      // Blockquotes (> Quote)
      if (line.startsWith('> ')) {
        const quoteLines = [];
        while (i < lines.length && lines[i].startsWith('> ')) {
          quoteLines.push(lines[i].substring(2));
          i++;
        }
        elements.push(
          <blockquote key={`quote-${i}`} className={styles.blockquote}>
            {parseInline(quoteLines.join(' '))}
          </blockquote>
        );
        continue;
      }
      
      // Horizontal rule (---)
      if (line.trim() === '---' || line.trim() === '***') {
        elements.push(<hr key={`hr-${i}`} className={styles.divider} />);
        i++;
        continue;
      }
      
      // Empty lines
      if (line.trim() === '') {
        i++;
        continue;
      }
      
      // Regular paragraphs
      elements.push(
        <p key={`p-${i}`} className={styles.paragraph}>
          {parseInline(line)}
        </p>
      );
      i++;
    }
    
    return elements;
  };
  
  // Parse inline markdown (bold, italic, links, code, etc.)
  const parseInline = (text) => {
    const parts = [];
    let remaining = text;
    let key = 0;
    
    while (remaining.length > 0) {
      // Inline code (`code`)
      const codeMatch = remaining.match(/`([^`]+)`/);
      if (codeMatch) {
        const before = remaining.substring(0, codeMatch.index);
        if (before) parts.push(before);
        
        parts.push(
          <code key={`code-${key++}`} className={styles.inlineCode}>
            {codeMatch[1]}
          </code>
        );
        
        remaining = remaining.substring(codeMatch.index + codeMatch[0].length);
        continue;
      }
      
      // Bold (**text** or __text__)
      const boldMatch = remaining.match(/(\*\*|__)(.+?)\1/);
      if (boldMatch) {
        const before = remaining.substring(0, boldMatch.index);
        if (before) parts.push(before);
        
        parts.push(
          <strong key={`bold-${key++}`} className={styles.bold}>
            {boldMatch[2]}
          </strong>
        );
        
        remaining = remaining.substring(boldMatch.index + boldMatch[0].length);
        continue;
      }
      
      // Italic (*text* or _text_)
      const italicMatch = remaining.match(/(\*|_)(.+?)\1/);
      if (italicMatch) {
        const before = remaining.substring(0, italicMatch.index);
        if (before) parts.push(before);
        
        parts.push(
          <em key={`italic-${key++}`} className={styles.italic}>
            {italicMatch[2]}
          </em>
        );
        
        remaining = remaining.substring(italicMatch.index + italicMatch[0].length);
        continue;
      }
      
      // Links ([text](url))
      const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);
      if (linkMatch) {
        const before = remaining.substring(0, linkMatch.index);
        if (before) parts.push(before);
        
        parts.push(
          <a 
            key={`link-${key++}`} 
            href={linkMatch[2]} 
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {linkMatch[1]}
          </a>
        );
        
        remaining = remaining.substring(linkMatch.index + linkMatch[0].length);
        continue;
      }
      
      // Plain URLs (https://...)
      const urlMatch = remaining.match(/(https?:\/\/[^\s]+)/);
      if (urlMatch) {
        const before = remaining.substring(0, urlMatch.index);
        if (before) parts.push(before);
        
        parts.push(
          <a 
            key={`url-${key++}`} 
            href={urlMatch[1]} 
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {urlMatch[1]}
          </a>
        );
        
        remaining = remaining.substring(urlMatch.index + urlMatch[0].length);
        continue;
      }
      
      // No more special formatting
      parts.push(remaining);
      break;
    }
    
    return parts;
  };
  
  return (
    <div className={styles.markdown}>
      {parseMarkdown(content)}
    </div>
  );
};

export default MarkdownRenderer;